import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../components/ui/Panel';
import Pill from '../components/ui/Pill';
import Button from '../components/ui/Button';
import { ErrorNotice, LoadingPanel, EmptyState, InfoNotice } from '../components/ui/States';
import AcceptFormModal from '../components/forms/AcceptFormModal';
import { fmt, relative, utcStamp, dateShort } from '../lib/format';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';
import { EditionSummary, FormsOverview, apiError, editionName } from '../lib/forms';

function suggestionText(e: EditionSummary, names: Map<string, string>) {
  const s = e.suggestion;
  if (!s) return 'No suggestion — decide from the form’s questions';
  if (s.ignore) return `Looks like something to ignore (${s.reason})`;
  if (!s.familyKey) return 'No suggestion';
  const parts = [`Looks like ${names.get(s.familyKey) ?? s.familyKey}`];
  if (s.year) parts.push(String(s.year));
  if (s.copiedFrom) parts.push(`copied from ${s.copiedFrom.label}`);
  return parts.join(', ');
}

function FamilyCardView({ card, onOpen }: { card: FormsOverview['families'][number]; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="bg-white text-left px-5 pt-4 pb-5 hover:bg-selected flex flex-col justify-between min-h-[164px]"
    >
      <div className="flex items-start justify-between gap-3">
        <span className="font-semibold text-table leading-tight">{card.name}</span>
        {card.exceptions > 0 ? (
          <span className="font-semibold rounded-sm bg-warning-tint text-warning text-small px-[6px] py-[2px] leading-none tabular shrink-0">
            {fmt(card.exceptions)}
          </span>
        ) : null}
      </div>
      <div>
        <div className="font-semibold tabular text-[38px] leading-none mt-3">{fmt(card.entries)}</div>
        <div className="text-small text-ink-2 mt-2">
          {card.editions} {card.editions === 1 ? 'edition' : 'editions'}
          {card.thisWeek > 0 ? ` · +${fmt(card.thisWeek)} this week` : ''}
        </div>
        <div className="text-small text-ink-2 mt-[2px]">
          {card.exceptions > 0
            ? [
                card.unmatched ? `${fmt(card.unmatched)} unmatched` : null,
                card.newQuestions ? `${fmt(card.newQuestions)} new questions` : null,
                card.flagged ? `${fmt(card.flagged)} odd answers` : null,
                card.missing ? `${fmt(card.missing)} gone upstream` : null,
              ]
                .filter(Boolean)
                .join(' · ')
            : card.identity === 'name' && card.nameOnly
              ? `${fmt(card.nameOnly)} wait for name matching`
              : 'Nothing to resolve'}
        </div>
      </div>
    </button>
  );
}

export default function WebsiteForms() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const mutable = canMutate(user?.role);
  const [accepting, setAccepting] = useState<EditionSummary | null>(null);
  const [showIgnored, setShowIgnored] = useState(false);

  const { data, isLoading, error } = useQuery<FormsOverview>(
    'forms-overview',
    async () => (await api.get('/forms/overview')).data.data,
  );
  const ignoredList = useQuery<EditionSummary[]>(
    ['forms-editions', 'ignored'],
    async () => (await api.get('/forms/editions', { params: { status: 'ignored' } })).data.data,
    { enabled: showIgnored },
  );

  const refresh = () => {
    queryClient.invalidateQueries('forms-overview');
    queryClient.invalidateQueries('forms-editions');
    queryClient.invalidateQueries('dashboard');
  };
  const ignore = useMutation(async (id: string) => api.post(`/forms/editions/${id}/ignore`), { onSuccess: refresh });
  const unignore = useMutation(async (id: string) => (await api.post(`/forms/editions/${id}/unignore`)).data.data, {
    onSuccess: refresh,
  });
  const sync = useMutation(async () => (await api.post('/forms/sync', { mode: 'nightly' })).data.data, {
    onSuccess: () => {
      setTimeout(refresh, 3000);
    },
  });

  const familyNames = new Map((data?.families ?? []).map((f) => [f.key, f.name]));

  return (
    <>
      <PageHeader
        title="Website forms"
        sub="Every Gravity Forms submission, grouped into families. New forms are found on their own; you are only asked about forms that need a family and about exceptions."
        aside={
          <div className="text-right shrink-0">
            {mutable ? (
              <Button
                variant="outline"
                size="sm"
                disabled={!data?.enabled || sync.isLoading}
                onClick={() => sync.mutate()}
              >
                {sync.isLoading ? 'Starting…' : 'Pull from WordPress now'}
              </Button>
            ) : null}
            <div className="text-small text-ink-2 mt-2">
              {data?.lastSync
                ? `Last pull ${utcStamp(data.lastSync.startedAt)} UTC · ${data.lastSync.status}`
                : data && !data.enabled
                  ? 'Nightly pull not configured'
                  : 'No pull recorded yet'}
            </div>
          </div>
        }
      />

      {error ? <ErrorNotice>Could not load website forms.</ErrorNotice> : null}
      {sync.isError ? <ErrorNotice>{apiError(sync.error, 'The pull could not start.')}</ErrorNotice> : null}
      {sync.data?.started ? (
        <div className="mb-4">
          <InfoNotice>Pull started. It reads WordPress slowly on purpose; counts update as it goes.</InfoNotice>
        </div>
      ) : null}
      {data?.lastSync?.status === 'failed' ? (
        <div className="mb-4">
          <ErrorNotice>The last pull failed: {data.lastSync.errorMessage}</ErrorNotice>
        </div>
      ) : null}

      <Panel>
        <PanelHeader title="Needs a decision" note={data ? `${data.needsDecision.length} forms` : undefined} />
        {isLoading || !data ? (
          <LoadingPanel label="Loading forms…" height={120} />
        ) : data.needsDecision.length === 0 ? (
          <PanelBody>
            <p className="text-table text-ink-2">
              Every form with entries has a family or is ignored. {fmt(data.waiting)} empty forms are waiting silently
              for their first entry.
            </p>
          </PanelBody>
        ) : (
          <div>
            {data.needsDecision.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between gap-4 px-5 py-[14px] border-b border-line last:border-b-0"
              >
                <div className="min-w-0">
                  <div className="text-table font-semibold truncate">
                    {e.title ?? `Form ${e.formId}`} <span className="text-ink-2 font-normal">· form {e.formId}</span>
                  </div>
                  <div className="text-small text-ink-2 mt-[2px]">
                    {fmt(e.entries || e.upstreamEntryCount || 0)} entries
                    {e.lastEntryAt ? ` · last ${relative(e.lastEntryAt)}` : ''} · {suggestionText(e, familyNames)}
                  </div>
                </div>
                {mutable ? (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" variant="primary" onClick={() => setAccepting(e)}>
                      Accept…
                    </Button>
                    <Button size="sm" variant="ghost" disabled={ignore.isLoading} onClick={() => ignore.mutate(e.id)}>
                      Ignore
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Panel>

      <div className="mt-6 border-y border-line-strong">
        {isLoading || !data ? (
          <LoadingPanel label="Loading families…" height={200} />
        ) : (
          <div className="grid gap-px bg-line" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {data.families.map((card) => (
              <FamilyCardView key={card.key} card={card} onOpen={() => navigate(`/forms/${card.key}`)} />
            ))}
          </div>
        )}
      </div>

      <Panel className="mt-6">
        <PanelHeader
          title="Ignored forms"
          note={data ? `${data.ignored.count} forms · ${fmt(data.ignored.entries)} entries not stored` : undefined}
        >
          <button onClick={() => setShowIgnored((v) => !v)} className="font-semibold text-small link-underline">
            {showIgnored ? 'Hide' : 'Show'}
          </button>
        </PanelHeader>
        {showIgnored ? (
          ignoredList.isLoading ? (
            <LoadingPanel label="Loading…" height={100} />
          ) : (ignoredList.data ?? []).length === 0 ? (
            <EmptyState title="Nothing ignored">No form is ignored.</EmptyState>
          ) : (
            <div>
              {(ignoredList.data ?? []).map((e) => (
                <div
                  key={e.id}
                  className="flex items-center justify-between gap-4 px-5 py-3 border-b border-line last:border-b-0"
                >
                  <div className="min-w-0">
                    <div className="text-table truncate">
                      {editionName(e)} <span className="text-ink-2">· form {e.formId}</span>
                    </div>
                    <div className="text-small text-ink-2">
                      {e.upstreamEntryCount === null
                        ? 'entry count not read yet'
                        : `${fmt(e.upstreamEntryCount)} entries in WordPress`}
                      {e.upstreamCreatedAt ? ` · created ${dateShort(e.upstreamCreatedAt)}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Pill tone="inactive">Ignored</Pill>
                    {mutable ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={unignore.isLoading}
                        onClick={() => unignore.mutate(e.id)}
                      >
                        Un-ignore
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <PanelBody>
            <p className="text-small text-ink-2">
              Entries of ignored forms are never stored. Un-ignoring a form re-reads its whole history from WordPress.
            </p>
          </PanelBody>
        )}
      </Panel>

      {accepting && data ? (
        <AcceptFormModal
          edition={accepting}
          families={data.families}
          onClose={() => setAccepting(null)}
          onDone={() => {
            setAccepting(null);
            refresh();
          }}
        />
      ) : null}
    </>
  );
}
